import React, { useMemo } from 'react';
import { Stack, Text, Group, Badge, ScrollArea, ActionIcon, Tooltip, Divider } from '@mantine/core';
import { AlertTriangle, CheckCircle, AlertCircle, Clock, XCircle } from 'lucide-react';
import { useDashboardData } from '../../../../hooks/dashboard/useDashboardData';
import { useUpdateTodo } from '../../../../hooks/useTodos';
import { getUrgencyLevel, getOverdueAge, isOverdue } from '../../../../utils/dashboard';
import { useNavigation } from '../../../../contexts/NavigationContext';
import { useIsTouch } from './useIsTouch';
import { useIsSmallScreen } from './useIsSmallScreen';

interface OverdueAnalysisProps {
  className?: string;
  onEditTodo?: (todoId: number) => void;
}

export const OverdueAnalysis: React.FC<OverdueAnalysisProps> = ({ className, onEditTodo }) => {
  const dashboardData = useDashboardData();
  const updateTodoMutation = useUpdateTodo();
  useNavigation(); // retain hook call if side-effects; no direct usage needed

  const isTouch = useIsTouch();
  const isSmall = useIsSmallScreen();
  const showTooltips = !(isTouch && isSmall);

  const overdueAnalysis = useMemo(() => {
    const overdueTasks = dashboardData.todos.filter(isOverdue);
    const critical = overdueTasks.filter(t => getUrgencyLevel(t) === 'critical');
    const sortedOverdue = overdueTasks.sort((a, b) => {
      const urgencyOrder = { critical: 3, warning: 2, normal: 1 } as const;
      const aUrgency = urgencyOrder[getUrgencyLevel(a)];
      const bUrgency = urgencyOrder[getUrgencyLevel(b)];
      if (aUrgency !== bUrgency) return bUrgency - aUrgency;
      return getOverdueAge(b) - getOverdueAge(a);
    });
    const overdueByList = dashboardData.todoLists.map(list => ({
      list,
      overdueCount: overdueTasks.filter(t => t.listId === list.id).length,
      criticalCount: critical.filter(t => t.listId === list.id).length
    })).filter(x => x.overdueCount > 0).sort((a,b)=>b.overdueCount - a.overdueCount);
    return {
      total: overdueTasks.length,
      sortedTasks: sortedOverdue.slice(0,10),
      listBreakdown: overdueByList
    };
  }, [dashboardData.todos, dashboardData.todoLists]);

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'critical': return 'red';
      case 'warning': return 'yellow';
      case 'normal': return 'blue';
      default: return 'gray';
    }
  };

  const getUrgencyIcon = (urgency: string) => {
    switch (urgency) {
      case 'critical': return <XCircle size={16} />;
      case 'warning': return <AlertCircle size={16} />;
      case 'normal': return <Clock size={16} />;
      default: return <AlertTriangle size={16} />;
    }
  };

  const handleCompleteTask = async (taskId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await updateTodoMutation.mutateAsync({
        id: taskId,
        data: { isCompleted: true }
      });
    } catch (error) {
      console.error('Failed to complete task:', error);
    }
  };


  if (overdueAnalysis.total === 0) {
    return (
      <Stack gap="xs" className={className}>
        <Group gap={6}>
          <CheckCircle size={16} style={{ color: 'var(--mantine-color-green-6)' }} />
          <Text size="sm" fw={600}>Overdue</Text>
          <Badge size="xs" variant="light" color="green">0</Badge>
        </Group>
        <Text size="xs" c="dimmed">All clear</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="xs" className={className}>
      <Group gap={6}>
        <AlertTriangle size={16} style={{ color: 'var(--mantine-color-red-6)' }} />
        <Text size="sm" fw={600}>Overdue</Text>
        {showTooltips ? (
          <Tooltip label="Total overdue tasks" withArrow>
            <Badge size="xs" variant="filled" color="red">{overdueAnalysis.total}</Badge>
          </Tooltip>
        ) : (
          <Badge size="xs" variant="filled" color="red">{overdueAnalysis.total}</Badge>
        )}
      </Group>
      <ScrollArea h={210} offsetScrollbars>
        <Stack gap={6}>
          {overdueAnalysis.sortedTasks.slice(0,6).map(task => {
            const urgency = getUrgencyLevel(task);
            const urgencyColor = getUrgencyColor(urgency);
            const urgencyIcon = getUrgencyIcon(urgency);
            const overdueAge = getOverdueAge(task);
            return (
              <div
                key={task.id}
                className="overdue-task-row"
                data-dark-style
                data-urgency={urgency}
                style={{
                  ['--urgency-color' as any]: `var(--mantine-color-${urgencyColor}-6)`,
                  ['--urgency-glow' as any]: `var(--mantine-color-${urgencyColor}-3)`,
                  ['--urgency-bg' as any]: `var(--mantine-color-${urgencyColor}-0)`,
                  cursor: 'pointer'
                }}
                onClick={() => onEditTodo?.(task.id)}
              >
                <Group gap={6} wrap="nowrap" align="flex-start">
                  {showTooltips ? (
                    <Tooltip label={`Overdue for ${overdueAge} day${overdueAge===1?'':'s'} · ${urgency.toUpperCase()}`} withArrow>
                      <Badge size="xs" variant="filled" color={urgencyColor} leftSection={urgencyIcon}>{overdueAge}d</Badge>
                    </Tooltip>
                  ) : (
                    <Badge size="xs" variant="filled" color={urgencyColor} leftSection={urgencyIcon}>{overdueAge}d</Badge>
                  )}
                  <Text size="xs" fw={600} style={{ lineHeight:1.2, flex:1 }} lineClamp={1}>{task.title}</Text>
                </Group>
                <Group gap={6} justify="space-between" wrap="nowrap" style={{ paddingLeft:2 }}>
                  {showTooltips ? (
                    <Tooltip label={task.listName} withArrow>
                      <Badge size="xs" variant="light" color="gray" style={{ maxWidth:140 }}>
                        {task.listName.slice(0,18)}{task.listName.length>18?'…':''}
                      </Badge>
                    </Tooltip>
                  ) : (
                    <Badge size="xs" variant="light" color="gray" style={{ maxWidth:140 }}>
                      {task.listName.slice(0,18)}{task.listName.length>18?'…':''}
                    </Badge>
                  )}
                  <ActionIcon size="sm" variant="light" color="green" onClick={(e)=>handleCompleteTask(task.id,e)} loading={updateTodoMutation.isPending}>
                    <CheckCircle size={14} />
                  </ActionIcon>
                </Group>
              </div>
            );
          })}
        </Stack>
      </ScrollArea>
      {overdueAnalysis.listBreakdown.length > 0 && (
        <Stack gap={4}>
          <Group gap={4} wrap="wrap">
            {overdueAnalysis.listBreakdown.slice(0,4).map(item => (
              showTooltips ? (
                <Tooltip key={item.list.id} label={`${item.list.name}: ${item.overdueCount} overdue (${item.criticalCount} critical)`} withArrow>
                  <Badge size="xs" variant="light" color={item.criticalCount>0? 'red':'gray'}>{item.list.name}:{item.overdueCount}</Badge>
                </Tooltip>
              ) : (
                <Badge key={item.list.id} size="xs" variant="light" color={item.criticalCount>0? 'red':'gray'}>{item.list.name}:{item.overdueCount}</Badge>
              )
            ))}
          </Group>
        </Stack>
      )}
      {isTouch && isSmall && (
        <>
          <Divider my={4} />
          <Group gap={4} wrap="wrap" style={{ fontSize:10, lineHeight:1.2, alignItems:'center' }} c="dimmed">
            <Text size="10px" fw={500}>Legend:</Text>
            <Group gap={4} style={{ alignItems:'center' }}>
              <span style={{ width:10, height:10, background:'var(--mantine-color-red-6)', borderRadius:2, display:'inline-block' }} />
              <Text size="10px">Critical</Text>
            </Group>
            <Group gap={4} style={{ alignItems:'center' }}>
              <span style={{ width:10, height:10, background:'var(--mantine-color-yellow-5)', borderRadius:2, display:'inline-block' }} />
              <Text size="10px">Warning</Text>
            </Group>
            <Group gap={4} style={{ alignItems:'center' }}>
              <span style={{ width:10, height:10, background:'var(--mantine-color-blue-6)', borderRadius:2, display:'inline-block' }} />
              <Text size="10px">Normal</Text>
            </Group>
            <Text size="10px">d=days overdue</Text>
          </Group>
        </>
      )}
    </Stack>
  );
};